import React, { useMemo, useState } from "react";
import { graphql } from "gatsby";
import FaqWashPass from "./faq-washpass";

import SliderHero from "./slider-hero";
import SliderProdotti from "./slider-prodotti";
import EditorialIntro from "./editorial-intro";
import Newsletter from "./newsletter";
import ItemsGrid from "./items-grid";
import SliderGallery from "./slider-gallery";
import SliderGamma from "./slider-gamma";
import Intro from "./intro";
import OnlineStore from "./online-store";
import Assistance from "./assistance";
import StoreLocationWrapper from "./store-location-wrapper";
import Form from "./form";
import Manuali from "./manuali";
import withPreview from "../utility/with-preview";
import classNames from "classnames";
import AssistanceFooter from "./assistance-footer";
import safeSlugify from "../utility/safe-slugify";
import Pin from "./pin";
import HtmlTextField from "./html-text-field";
import CategoryModule from "./category-module";

const RawLiferayComponent = ({
  data,
  config,
  labelManagerLabels,
  location,
  origin,
  breadcrumb,
  isFirst,
}) => (
  <React.Fragment>
    {
      {
        LiferaySliderHero: <SliderHero data={data} breadcrumb={breadcrumb} isFirst={isFirst} />,
        LiferayFeaturedProduct: (
          <SliderProdotti
            data={data}
            location={location}
            config={config}
            origin={origin}
            labelManagerLabels={labelManagerLabels}
            isFirst={isFirst}
          />
        ),
        LiferayNewsletter: <Newsletter data={data} />,
        LiferayEditorial: <EditorialIntro data={data} breadcrumb={breadcrumb} isFirst={isFirst} />,
        LiferayDynamicTriplettaListing: <ItemsGrid data={data} isProduct={false} />,
        LiferayGallery: <SliderGallery data={data} isFirst={isFirst} />,
        LiferayCompare: (
          <SliderGamma
            data={data}
            labelManagerLabels={labelManagerLabels}
            config={config}
            origin={origin}
            location={location}
            isFirst={isFirst}
          />
        ),
        LiferayIntro: <Intro data={data} breadcrumb={breadcrumb} isFirst={isFirst} />,
        LiferayOnlineStore: <OnlineStore data={data} />,
        LiferayAssistance: <Assistance data={data} />,
        LiferayStoreLocation: <StoreLocationWrapper data={data} location={location} />,
        LiferayFormConfiguration: <Form data={data} location={location} />,
        LiferayBooklet: <Manuali data={data} labelManagerLabels={labelManagerLabels} />,
        LiferayFaq: <FaqWashPass data={data} isFirst={isFirst} />,
        LiferayAssistanceFooter: <AssistanceFooter data={data} />,
        LiferayPin: <Pin data={data} isFirst={isFirst} />,
        LiferayHtmlTextField: <HtmlTextField data={data} />,
        LiferayCategoryModule: <CategoryModule data={data} />,
      }[data.__typename]
    }
  </React.Fragment>
);

const LiferayComponent = withPreview(RawLiferayComponent);

const Switch = ({ data, switchModule, isFirst, config, labelManagerLabels, pagePath }) => {
  const sections = useMemo(
    () =>
      data.tabs
        .filter(
          (item) =>
            item?.contents &&
            item.contents?.length > 0 &&
            item.contents
              ?.sort((a, b) => {
                const posA = parseInt(a?.webContentPosition);
                const posB = parseInt(b?.webContentPosition);
                return !isNaN(posA) && !isNaN(posB)
                  ? posA - posB
                  : isNaN(posA) && isNaN(posB)
                  ? 0
                  : isNaN(posA)
                  ? 1
                  : -1;
              })
              .map((item) => item)
              .filter(Boolean)
        )
        .sort((a, b) => {
          const posA = parseInt(a?.position);
          const posB = parseInt(b?.position);
          return !isNaN(posA) && !isNaN(posB)
            ? posA - posB
            : isNaN(posA) && isNaN(posB)
            ? 0
            : isNaN(posA)
            ? 1
            : -1;
        }),
    [data]
  );

  const [currentTab, setCurrentTab] = useState(0);

  const toggleTab = (e) => {
    const index = parseInt(e.currentTarget.parentNode.getAttribute("data-index"));
    setCurrentTab(index);
  };

  return (
    <>
      {data?.anchorTitleDesktop && (
        <span id={safeSlugify(data.anchorTitleDesktop)} className="tabs__scroll-anchor" />
      )}
      <div
        className={classNames(
          "tab-wrapper",
          data?.backgroundColor && `tab-wrapper--${data.backgroundColor}`,
          switchModule && "tab-wrapper--negative"
        )}
      >
        {data?.titleSwitch &&
          (isFirst ? (
            <h1 className="items-grid__title">{data.titleSwitch}</h1>
          ) : (
            <h2 className="items-grid__title">{data.titleSwitch}</h2>
          ))}
        {data?.introductionText && (
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div
                  className="items-grid__subtitle text-center mx-auto"
                  dangerouslySetInnerHTML={{ __html: data.introductionText }}
                ></div>
              </div>
            </div>
          </div>
        )}
        <>
          <div
            className={classNames(
              "listing__cta-wrapper",
              data?.backgroundColor && `listing__cta-wrapper--${data.backgroundColor}`
            )}
          >
            {sections.map((item, idx) => (
              <div role="tab" className="listing__cta" key={idx} data-index={idx}>
                <button
                  className={classNames("btn-link btn-link--white", currentTab === idx && "active")}
                  onClick={toggleTab}
                >
                  {item.tabName}
                </button>
              </div>
            ))}
          </div>
          {sections.map(
            (item, idx) =>
              currentTab === idx &&
              item.contents
                .filter((item2) => !!item2?.webContent)
                .map((item2, j) => (
                  <section key={j}>
                    <LiferayComponent
                      data={item2?.webContent}
                      config={config}
                      labelManagerLabels={labelManagerLabels}
                      pagePath={pagePath}
                    />
                  </section>
                ))
          )}
        </>
      </div>
    </>
  );
};

export default Switch;
export const fragment = graphql`
  fragment SwitchFragment on LiferaySwitch {
    id
    anchorTitleMobile
    anchorTitleDesktop
    titleSwitch
    introductionText
    backgroundColor
    tabs {
      tabName
      position
      contents {
        webContent {
          __typename
          ...SliderHeroFragment
          ...FeaturedProductFragment
          ...BlogPageFragment
          ...ServiceFragment
          ...NewsletterFragment
          ...EditorialFragment
          ...DynamicTriplettaListingFragment
          ...GalleryFragment
          ...CompareFragment
          ...IntroFragment
          ...OnlineStoreFragment
          ...AssistanceFragment
          ...AssistanceFooterFragment
          ...StoreLocationFragment
          ...FormConfigurationFragment
          ...BookletFragment
          ...FaqWashPassFragment
          ...PinFragment
          ...HtmlTextFieldFragment
          ...CategoryModuleFragment
        }
        webContentPosition
      }
    }
  }
`;
